/**
 * E5 — where new couples say they came from, and which planners sent them.
 *
 * ⚠️ `(not asked)` IS NOT A CHANNEL. It is the absence of an answer, and a
 * donut is a claim that its slices partition one whole: putting "not asked"
 * beside Instagram would make "we never asked" look like the biggest marketing
 * channel LifeScribe has. It is excluded from the chart and stated as a
 * sentence underneath, which is what it is — a coverage caveat on the mix
 * above, not a competitor to it.
 *
 * ⚠️ THIS IS EMPTY ON DAY ONE, ON PURPOSE. Nothing writes `acquisition_source`
 * until the wedding-book setup wizard's "How did you hear about us?" step
 * ships, so until then EVERY book is `(not asked)` and there is no mix to draw.
 * That state has to read as "the question has not been asked yet", never as a
 * chart that failed to load — an operator who sees a blank card learns to
 * distrust the card, and this one will be blank for weeks.
 *
 * The planner list below is a LEAD LIST, NOT A LEDGER. The fold is
 * `lower(trim(…))` and nothing else, so two spellings of one firm are two rows.
 * The server ships that caveat as `note` and it is rendered verbatim rather
 * than restated here, because a restatement is a second copy that can drift.
 *
 * ZERO NEW HEX: every slice colour is a `brandColors` constant.
 */

import React from 'react';
import { Box, Card, CardContent, Stack, Typography, useTheme } from '@mui/material';
import { PieChart, Pie, Cell, Tooltip, ResponsiveContainer } from 'recharts';
import { brandColors } from '../../../theme/brandColors';
import { centsToUsd, num } from '../weddingFormat';
import type { MembersFirstTouch } from '../weddingApi';

/** The wizard's answers, spelled the way a person says them. */
const SOURCE_LABEL: Record<string, string> = {
  instagram: 'Instagram',
  tiktok: 'TikTok',
  friends_book: "A friend's book",
  planner_referral: 'Planner referral',
  google_search: 'Google search',
  other: 'Other',
  '(not asked)': 'Not asked',
  '(none)': 'No source recorded',
};

export function sourceLabel(key: string): string {
  return SOURCE_LABEL[key] || key;
}

/** The server's key for "this book predates the question". Never a slice. */
export const NOT_ASKED = '(not asked)';

const SLICE_COLOR: Record<string, string> = {
  instagram: brandColors.richBurgundy,
  tiktok: brandColors.deepTeal,
  friends_book: brandColors.warmGold,
  planner_referral: brandColors.forestGreen,
  google_search: brandColors.deepTealLight,
  other: brandColors.slateGray,
};

/** For a source key the wizard grows later — palette constants, in order. */
const SPARE_COLORS: string[] = [
  brandColors.warmGoldDark,
  brandColors.deepTealDark,
  brandColors.warmGoldLight,
  brandColors.slateGray,
];

interface Props {
  firstTouch?: MembersFirstTouch;
  /** "the last 30 days" — the same phrase the rest of the page uses. */
  rangeLabel: string;
}

const AcquisitionMixCard: React.FC<Props> = ({ firstTouch, rangeLabel }) => {
  const theme = useTheme();

  const sources = firstTouch?.sources || [];

  const slices = React.useMemo(
    () =>
      sources
        .filter((s) => s.source !== NOT_ASKED)
        .map((s, i) => ({
          key: s.source,
          name: sourceLabel(s.source),
          books: num(s.books_in_range),
          color: SLICE_COLOR[s.source] || SPARE_COLORS[i % SPARE_COLORS.length],
        }))
        .filter((s) => s.books > 0)
        .sort((a, b) => b.books - a.books),
    [sources]
  );

  const notAsked = num(sources.find((s) => s.source === NOT_ASKED)?.books_in_range ?? 0);
  const answered = slices.reduce((sum, s) => sum + s.books, 0);

  const planners = firstTouch?.planner_referrals;

  return (
    <Card sx={{ borderRadius: '16px' }}>
      <CardContent>
        <Typography variant="h6" sx={{ fontWeight: 700, mb: 0.5 }}>
          Where members come from
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          New couples in {rangeLabel}, by first touch.
        </Typography>

        {!firstTouch?.available ? (
          <Typography variant="body2" color="text.secondary">
            {firstTouch?.reason
              || 'Couples are not yet asked how they heard about LifeScribe, so there is no first-touch breakdown.'}
          </Typography>
        ) : slices.length === 0 ? (
          /*
            THE DAY-ONE STATE. Two different nothings, and they are not the same
            fact: nobody started a book, versus books were started before anyone
            was asked. Only the second one is about the question.
          */
          <Typography variant="body2" color="text.secondary">
            {notAsked > 0
              ? `No answer has been recorded yet. All ${notAsked.toLocaleString()} book${
                notAsked === 1 ? '' : 's'
              } started in ${rangeLabel} ${notAsked === 1 ? 'was' : 'were'} created before the `
                + '“How did you hear about us?” question was asked, so there is no mix to show yet.'
              : 'No couple started a book in this range.'}
          </Typography>
        ) : (
          <>
            <Box sx={{ height: 200, width: '100%' }} data-testid="acquisition-donut">
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie
                    data={slices}
                    dataKey="books"
                    nameKey="name"
                    innerRadius={50}
                    outerRadius={82}
                    paddingAngle={2}
                  >
                    {slices.map((slice) => (
                      <Cell key={slice.key} fill={slice.color} />
                    ))}
                  </Pie>
                  <Tooltip
                    formatter={(value) => `${Number(value).toLocaleString()} books`}
                    contentStyle={{
                      backgroundColor: theme.palette.background.paper,
                      border: `1px solid ${theme.palette.divider}`,
                      color: theme.palette.text.primary,
                    }}
                  />
                </PieChart>
              </ResponsiveContainer>
            </Box>

            <Stack spacing={1} sx={{ mt: 1 }}>
              {slices.map((slice) => (
                <Box
                  key={slice.key}
                  sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1 }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, overflow: 'hidden' }}>
                    <Box
                      sx={{
                        width: 10,
                        height: 10,
                        flexShrink: 0,
                        borderRadius: '50%',
                        backgroundColor: slice.color,
                      }}
                    />
                    <Typography variant="body2" noWrap>{slice.name}</Typography>
                  </Box>
                  <Typography variant="body2" sx={{ fontWeight: 600 }}>
                    {slice.books.toLocaleString()}
                  </Typography>
                </Box>
              ))}
            </Stack>

            <Typography variant="caption" color="text.disabled" sx={{ display: 'block', mt: 1.5 }}>
              {notAsked > 0
                ? `${answered.toLocaleString()} of ${(answered + notAsked).toLocaleString()} books `
                  + 'answered. '
                  + (notAsked === 1
                    ? '1 book was created before the question was asked and is not in the mix.'
                    : `${notAsked.toLocaleString()} books were created before the question was `
                      + 'asked and are not in the mix.')
                : 'Every book in this range has an answer.'}
            </Typography>
          </>
        )}

        <Typography variant="subtitle2" sx={{ fontWeight: 700, mt: 3, mb: 1 }}>
          Planner referrals
        </Typography>

        {!planners ? (
          <Typography variant="body2" color="text.secondary">
            Planner referrals are not in this response.
          </Typography>
        ) : !planners.available ? (
          <Typography variant="body2" color="text.secondary">
            {planners.reason
              || 'Couples are not yet asked which planner referred them, so no planner can be named.'}
          </Typography>
        ) : planners.planners.length === 0 ? (
          <Typography variant="body2" color="text.secondary">
            No couple has named a planner yet.
          </Typography>
        ) : (
          <Stack spacing={1.25}>
            {planners.planners.map((row) => (
              <Box
                key={row.planner}
                sx={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', gap: 1 }}
              >
                <Typography variant="body2" noWrap>{row.planner_as_written}</Typography>
                <Typography variant="body2" color="text.secondary" sx={{ whiteSpace: 'nowrap' }}>
                  {num(row.books).toLocaleString()} book{num(row.books) === 1 ? '' : 's'}
                  {' · '}
                  <Box component="span" sx={{ fontWeight: 600, color: 'text.primary' }}>
                    {centsToUsd(row.paid_cents)}
                  </Box>
                </Typography>
              </Box>
            ))}
          </Stack>
        )}

        {/* The server's caveat, VERBATIM — a restatement here would be a second
            copy of one rule, free to drift from the fold the SQL actually does. */}
        {planners?.note && (
          <Typography variant="caption" color="text.disabled" sx={{ display: 'block', mt: 1.5 }}>
            {planners.note}
          </Typography>
        )}
      </CardContent>
    </Card>
  );
};

export default AcquisitionMixCard;
